import {
  Artifact,
  ArtifactChecklistAnswer,
  ArtifactChecklistItem,
  ArtifactSubTask,
  Response,
} from "@core/model";
import {ApiBuilder} from "..";

const addArtifactTask = (builder: ApiBuilder) =>
  builder.mutation<Artifact, {id: string; task: ArtifactSubTask}>({
    query: request => ({
      url: `/api/artifact/${request.id}/tasks`,
      method: "PUT",
      body: request.task,
    }),
    transformResponse: (response: Response<Artifact>) => {
      return response.data;
    },
    invalidatesTags: (result, error, {id}) => [
      {type: "artifact", id},
      {type: "artifact", id: "LIST"},
    ],
  });

const updateArtifactTask = (builder: ApiBuilder) =>
  builder.mutation<Artifact, {id: string; task: ArtifactSubTask}>({
    query: request => ({
      url: `/api/artifact/${request.id}/tasks/${request.task.id}`,
      method: "PUT",
      body: request.task,
    }),
    transformResponse: (response: Response<Artifact>) => {
      return response.data;
    },
    invalidatesTags: (result, error, {id}) => [
      {type: "artifact", id},
      {type: "artifact", id: "LIST"},
    ],
  });

const deleteArtifactTask = (builder: ApiBuilder) =>
  builder.mutation<void, {artifactId: string; taskId: string}>({
    query: ({artifactId, taskId}) => ({
      url: `/api/artifact/${artifactId}/tasks/${taskId}`,
      method: "DELETE",
    }),
    invalidatesTags: (_result, _error, {artifactId}) => [
      {type: "artifact", id: artifactId},
      {type: "artifact", id: "LIST"},
    ],
  });

const updateArtifactTaskChecklist = (builder: ApiBuilder) =>
  builder.mutation<
    Artifact,
    {artifactId: string; taskId: string; checklist: ArtifactChecklistItem[]}
  >({
    query: ({artifactId, taskId, checklist}) => ({
      url: `/api/artifact/${artifactId}/tasks/${taskId}/checklist`,
      method: "PUT",
      body: checklist,
    }),
    transformResponse: (response: Response<Artifact>) => {
      return response.data;
    },
    invalidatesTags: (_result, _error, {artifactId}) => [
      {type: "artifact", id: artifactId},
      {type: "artifact", id: "LIST"},
    ],
  });

const findAssignedArtifactTasks = (builder: ApiBuilder) =>
  builder.query<Artifact[], void>({
    query: () => ({
      url: "/api/artifact/assigned-tasks",
      method: "GET",
    }),
    transformResponse: (response: Response<Artifact[]>) => {
      return response.data;
    },
    providesTags: result => [
      {type: "artifact", id: "LIST"},
      ...(result ?? []).map(a => ({type: "artifact" as const, id: a.id})),
    ],
  });

/**
 * Imposta la risposta a uno step della checklist, o la cancella con answer null.
 */
const answerArtifactChecklistItem = (builder: ApiBuilder) =>
  builder.mutation<
    Artifact,
    {
      artifactId: string;
      taskId: string;
      itemId: string;
      answer: ArtifactChecklistAnswer | null;
    }
  >({
    query: ({artifactId, taskId, itemId, answer}) => ({
      url: `/api/artifact/${artifactId}/tasks/${taskId}/checklist/${itemId}/answer`,
      method: answer ? "PUT" : "DELETE",
      body: answer ?? undefined,
    }),
    transformResponse: (response: Response<Artifact>) => {
      return response.data;
    },
    invalidatesTags: (_result, _error, {artifactId}) => [
      {type: "artifact", id: artifactId},
      {type: "artifact", id: "LIST"},
    ],
  });

export {
  findAssignedArtifactTasks,
  answerArtifactChecklistItem,
  addArtifactTask,
  updateArtifactTask,
  deleteArtifactTask,
  updateArtifactTaskChecklist,
};
